import React, { useEffect, useMemo, useState } from "react";
import { api } from "../Assets/assetsShared";
import "./TimesheetMaster.css";

const EMPTY = {
  department: "",
  category: "",
  requiresProject: false,
  task: "",
  subTask: "",
};

export default function TimesheetMaster() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("All");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/timesheet-master");
      setRows(res.data || []);
    } catch (err) {
      setMessage(
        err.response?.data?.message || "Could not load the master data.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const departments = useMemo(
    () => Array.from(new Set(rows.map((r) => r.department))),
    [rows],
  );

  const countOf = (dept) => rows.filter((r) => r.department === dept).length;

  const visibleRows = rows.filter(
    (r) => filter === "All" || r.department === filter,
  );

  const change = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const addRow = async () => {
    if (saving) return;

    if (!form.department.trim() || !form.task.trim()) {
      setMessage("Department and Task are required.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/timesheet-master", {
        department: form.department.trim(),
        category: form.category.trim(),
        requiresProject: form.requiresProject,
        task: form.task.trim(),
        subTask: form.subTask.trim(),
      });
      setMessage("Row added.");
      setForm(EMPTY);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not add the row.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await api.delete(`/timesheet-master/${id}`);
      load();
    } catch (err) {
      setMessage("Could not delete the row.");
    }
  };

  const syncSheet = async () => {
    if (syncing) return;
    setSyncing(true);
    setMessage("");
    try {
      const res = await api.post("/timesheet-master/sync");
      setMessage(
        `Synced from Google Sheet: ${res.data.imported} new rows, ${res.data.skipped} already existed.`,
      );
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="tsm-page">
      {/* ----- banner ----- */}
      <header className="tsm-banner">
        <div>
          <h1>Timesheet Master</h1>
          <p>
            Softvolute departments, categories and tasks used in the Timesheet.
          </p>
        </div>

        <div className="tsm-stats">
          <div className="tsm-stat">
            <strong>{departments.length}</strong>
            <span>Departments</span>
          </div>
          <div className="tsm-stat">
            <strong>{rows.length}</strong>
            <span>Total Rows</span>
          </div>
        </div>
      </header>

      {message && <div className="tsm-message">{message}</div>}

      {/* ----- sync + add ----- */}
      <div className="tsm-top">
        <section className="tsm-card">
          <h3>Sync from Google Sheet</h3>
          <p className="tsm-hint">
            Reads the "Softvolute ERP" tab of the Google Sheet and adds the new
            rows to the table below. Existing rows are skipped.
          </p>
          <button
            className="tsm-btn tsm-btn-primary"
            onClick={syncSheet}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "Sync from Google Sheet"}
          </button>
        </section>

        <section className="tsm-card">
          <h3>Add New Row</h3>

          <div className="tsm-grid">
            <label className="tsm-field">
              <span>Department</span>
              <input
                placeholder="e.g. Accounts"
                value={form.department}
                onChange={(e) => change("department", e.target.value)}
              />
            </label>

            <label className="tsm-field">
              <span>Category</span>
              <input
                placeholder="e.g. Income"
                value={form.category}
                onChange={(e) => change("category", e.target.value)}
              />
            </label>

            <label className="tsm-field">
              <span>Task</span>
              <input
                placeholder="Task name"
                value={form.task}
                onChange={(e) => change("task", e.target.value)}
              />
            </label>

            <label className="tsm-field">
              <span>Sub-task</span>
              <input
                placeholder="Optional"
                value={form.subTask}
                onChange={(e) => change("subTask", e.target.value)}
              />
            </label>
          </div>

          <div className="tsm-form-foot">
            <label className="tsm-check">
              <input
                type="checkbox"
                checked={form.requiresProject}
                onChange={(e) => change("requiresProject", e.target.checked)}
              />
              Ask for a project when this task is selected
            </label>

            <button
              className="tsm-btn tsm-btn-primary"
              onClick={addRow}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Row"}
            </button>
          </div>
        </section>
      </div>

      {/* ----- table ----- */}
      <section className="tsm-card">
        <div className="tsm-table-head">
          <h3>Timesheet Master Table</h3>
          <span className="tsm-count">{visibleRows.length} rows</span>
        </div>

        <div className="tsm-tabs">
          <button
            type="button"
            className={`tsm-tab ${filter === "All" ? "active" : ""}`}
            onClick={() => setFilter("All")}
          >
            All <em>{rows.length}</em>
          </button>

          {departments.map((d) => (
            <button
              type="button"
              key={d}
              className={`tsm-tab ${filter === d ? "active" : ""}`}
              onClick={() => setFilter(d)}
            >
              {d} <em>{countOf(d)}</em>
            </button>
          ))}
        </div>

        <div className="tsm-table-wrap">
          <table className="tsm-table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Category</th>
                <th>Project</th>
                <th>Task</th>
                <th>Sub-task</th>
                <th className="tsm-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="tsm-empty">
                    Loading...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="tsm-empty">
                    No data yet. Click "Sync from Google Sheet" or add a row.
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <span className="tsm-dept">{r.department}</span>
                    </td>
                    <td>{r.category || "-"}</td>
                    <td>
                      {r.requiresProject ? (
                        <span className="tsm-badge">Required</span>
                      ) : (
                        <span className="tsm-dash">-</span>
                      )}
                    </td>
                    <td className="tsm-strong">{r.task}</td>
                    <td>{r.subTask || "-"}</td>
                    <td className="tsm-right">
                      <button
                        className="tsm-btn-delete"
                        onClick={() => remove(r._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
